import { useEffect } from 'react';
import { enableVadimTestMode } from '@gonfalon/dogfood-flags/dogfood-flags';
import { useThemeValue } from '@gonfalon/theme/useThemeValue';

// Side-effect import: registers the `.highlight-{light,dark,longhorn}-theme` CSS
// rules that this hook applies to `<body>`. Without it, the classname is set but
// no CSS variables are defined.
// oxlint-disable-next-line import/no-unassigned-import
import '../css/theme.css';

const THEME_CLASSES = ['highlight-light-theme', 'highlight-dark-theme', 'highlight-longhorn-theme'] as const;

const getThemeClassName = (theme: 'default' | 'dark', isLonghorn: boolean) => {
  if (isLonghorn) {
    return 'highlight-longhorn-theme';
  }
  return theme === 'dark' ? 'highlight-dark-theme' : 'highlight-light-theme';
};

let mountCount = 0;

/**
 * Applies the observability vanilla-extract theme class to document.body.
 * Required when rendering observability components (e.g. AgentInsightsPanel)
 * outside of ObservabilityRootLayout, since the Dialog portals to body
 * and needs the theme CSS variables to be defined there.
 *
 * Callers may nest: the class is only removed once the last caller unmounts.
 */
export const useObservabilityTheme = () => {
  const theme = useThemeValue();
  const isLonghorn = enableVadimTestMode();

  useEffect(() => {
    // `document.body` can be absent while the document is being torn down, so hold onto the
    // element the class was applied to rather than re-reading it during cleanup.
    const body = document.body;
    if (!body) {
      return;
    }

    const themeClass = getThemeClassName(theme, isLonghorn);
    mountCount += 1;
    body.classList.remove(...THEME_CLASSES);
    body.classList.add(themeClass);

    return () => {
      mountCount -= 1;
      if (mountCount === 0) {
        body.classList.remove(...THEME_CLASSES);
      }
    };
  }, [theme, isLonghorn]);

  return { isLonghorn };
};
